import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PortfolioSelector from './PortfolioSelector';
import { selectionFromParams, prefilled } from '../lib/reportPrefill';
import { defaultReportDate, maxReportDate } from '../lib/reportDates';
import ReportingCurrencyField from './ReportingCurrencyField';
import MaskedDateField from '../../../components/ui/MaskedDateField';

/**
 * Assets Under Management Popup
 *
 * One row per account for the whole firm, so an empty picker is the DEFAULT
 * scope (the Security Cross Reference precedent): all_accounts is the typed
 * backend flag and client_name '@@ALL' is the sentinel that survives
 * buildReportRequestParams. Picking a group or account narrows the list.
 *
 * Sort and filters are applied SERVER-side, so the screen, the print view, the
 * export, saved configurations and reruns all show the same rows. "Include
 * accrued interest" is display-only: the accrued memo column is always in the
 * payload and the box decides whether it prints (it replaced the separate
 * "w/ Accrued Interest" report on 2026-09-12).
 */
const SORT_OPTIONS = [
  { value: 'total', label: 'Total AUM' },
  { value: 'name', label: 'Portfolio name' },
  { value: 'port_code', label: 'Port code' },
  { value: 'open_date', label: 'Inception date' },
];

const AssetsUnderManagementPopup = ({
  isOpen,
  onClose,
  onConfirm,
  onCancel,
  reportInfo,
  initialParams = null,
  darkMode = false
}) => {
  const [selection, setSelection] = useState({ selectedItem: null, params: {} });
  const [date, setDate] = useState('');
  const [sortBy, setSortBy] = useState('total');
  const [sortDir, setSortDir] = useState('desc');
  const [openedFrom, setOpenedFrom] = useState('');
  const [openedTo, setOpenedTo] = useState('');
  const [minTotal, setMinTotal] = useState('');
  const [includeAccrued, setIncludeAccrued] = useState(false);
  const [currency] = useState('USD');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDate(prefilled(initialParams, 'date', defaultReportDate()));
      setSortBy(prefilled(initialParams, 'sort_by', 'total'));
      setSortDir(prefilled(initialParams, 'sort_dir', 'desc'));
      setOpenedFrom(prefilled(initialParams, 'opened_from', ''));
      setOpenedTo(prefilled(initialParams, 'opened_to', ''));
      setMinTotal(prefilled(initialParams, 'min_total', ''));
      setIncludeAccrued(prefilled(initialParams, 'include_accrued_interest', false) === true);
      setSelection({ selectedItem: null, params: {} });
      setErrors({});
    }
  }, [isOpen, initialParams]);

  // The single thin-params builder used by handleConfirm. Absent filters are
  // OMITTED rather than sent as null: the backend reads their presence.
  const buildParams = (sel) => ({
    date: date,
    currency: currency,
    sort_by: sortBy,
    sort_dir: sortDir,
    ...(openedFrom ? { opened_from: openedFrom } : {}),
    ...(openedTo ? { opened_to: openedTo } : {}),
    ...(minTotal !== '' ? { min_total: Number(minTotal) } : {}),
    include_accrued_interest: includeAccrued,
    ...(sel.selectedItem
      ? sel.params
      : { all_accounts: true, client_name: '@@ALL' }),
  });

  const validateForm = () => {
    const newErrors = {};

    // No portfolio requirement: an empty picker means firm-wide, this report's
    // default, not a missing field.

    if (!date) {
      newErrors.date = 'Date is required';
    }

    // Both dates are ISO 'YYYY-MM-DD', so a string compare IS a date compare.
    if (openedFrom && openedTo && openedFrom > openedTo) {
      newErrors.openedRange = 'Opened from must be on or before Opened to';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = async (submitted) => {
    // `submitted` is the Enter-resolved selection from PortfolioSelector (state
    // hasn't flushed yet); a button click passes a MouseEvent, so fall back to
    // state when it carries no selectedItem.
    const sel = (submitted && submitted.selectedItem) ? submitted : selection;
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await onConfirm(buildParams(sel));
      onClose();
    } catch (error) {
      console.error('Report generation error:', error);
      setErrors({ submit: error.message || 'Failed to generate report' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const labelCls = `block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`;
  const fieldCls = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
    darkMode
      ? 'bg-[#1a1a1a] border-white/10 text-white'
      : 'bg-white border-gray-300 text-gray-900'
  }`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`rounded-lg p-6 max-w-md w-full mx-4 shadow-xl ${
        darkMode ? 'bg-[#141414]' : 'bg-white'
      }`}>
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {reportInfo?.name || 'Assets Under Management'}
          </h3>
          <button
            onClick={onClose}
            className={`transition-colors ${
              darkMode
                ? 'text-gray-400 hover:text-gray-300'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {reportInfo?.description && (
          <div className={`mb-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {reportInfo.description}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <PortfolioSelector darkMode={darkMode} prefill="" initialSelection={selectionFromParams(initialParams)} onChange={setSelection} onSubmit={handleConfirm}  disabled={isLoading}/>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Leave empty to list every account firm-wide.
            </p>
          </div>

          <div>
            <label className={labelCls}>
              As Of Date <span className="text-red-500">*</span>
            </label>
            <MaskedDateField
              value={date}
              max={maxReportDate()}
              onChange={(e) => {
                setDate(e.target.value);
                if (errors.date) {
                  setErrors(prev => ({ ...prev, date: null }));
                }
              }}
              className={`${fieldCls} ${errors.date ? 'border-red-500' : ''}`}
              disabled={isLoading}
            />
            {errors.date && (
              <p className="text-red-500 text-xs mt-1">{errors.date}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Sort by</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={fieldCls}
                disabled={isLoading}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Order</label>
              <select
                value={sortDir}
                onChange={(e) => setSortDir(e.target.value)}
                className={fieldCls}
                disabled={isLoading}
              >
                <option value="desc">Decreasing (largest first)</option>
                <option value="asc">Increasing (smallest first)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Opened from</label>
              <MaskedDateField
                value={openedFrom}
                max={maxReportDate()}
                onChange={(e) => {
                  setOpenedFrom(e.target.value);
                  if (errors.openedRange) {
                    setErrors(prev => ({ ...prev, openedRange: null }));
                  }
                }}
                className={`${fieldCls} ${errors.openedRange ? 'border-red-500' : ''}`}
                disabled={isLoading}
              />
            </div>
            <div>
              <label className={labelCls}>Opened to</label>
              <MaskedDateField
                value={openedTo}
                max={maxReportDate()}
                onChange={(e) => {
                  setOpenedTo(e.target.value);
                  if (errors.openedRange) {
                    setErrors(prev => ({ ...prev, openedRange: null }));
                  }
                }}
                className={`${fieldCls} ${errors.openedRange ? 'border-red-500' : ''}`}
                disabled={isLoading}
              />
            </div>
          </div>
          {errors.openedRange && (
            <p className="text-red-500 text-xs -mt-2">{errors.openedRange}</p>
          )}

          <div>
            <label className={labelCls}>Minimum total</label>
            <input
              type="number"
              min="0"
              step="any"
              value={minTotal}
              placeholder="e.g. 1000000"
              onChange={(e) => setMinTotal(e.target.value)}
              className={fieldCls}
              disabled={isLoading}
            />
          </div>

          <label className={`flex items-center gap-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <input
              type="checkbox"
              checked={includeAccrued}
              onChange={(e) => setIncludeAccrued(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              disabled={isLoading}
            />
            Include accrued interest
          </label>

          <ReportingCurrencyField darkMode={darkMode} />

          {errors.submit && (
            <div className={`p-3 rounded-lg border ${
              darkMode
                ? 'bg-red-900/20 border-red-800'
                : 'bg-red-50 border-red-200'
            }`}>
              <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                {errors.submit}
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel || onClose}
            disabled={isLoading}
            className={darkMode ? 'border-gray-600 text-gray-300 hover:bg-[#1a1a1a]' : ''}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Generating...
              </>
            ) : (
              'OK'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssetsUnderManagementPopup;
